import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useTenant } from '@/hooks/useTenant';

export interface Product {
  id: string;
  channel_id: string | null;
  channel_name: string | null;
  name: string;
  sku: string | null;
  description: string | null;
  price_cents: number;
  stock_qty: number;
  min_stock: number;
  image_url: string | null;
  status: 'active' | 'inactive' | 'discontinued';
  created_at: string;
}

export interface ProductInput {
  channel_id?: string | null;
  name: string;
  sku?: string;
  description?: string;
  price_cents: number;
  stock_qty: number;
  min_stock: number;
  image_url?: string | null;
  status?: 'active' | 'inactive' | 'discontinued';
}

function translateProductError(msg: string): string {
  if (msg.includes('duplicate') || msg.includes('unique'))
    return 'Já existe um produto com esse SKU neste canal.';
  if (msg.includes('row-level security') || msg.includes('RLS') || msg.includes('new row violates'))
    return 'Permissão insuficiente para gerenciar produtos.';
  if (msg.includes('foreign key') || msg.includes('violates foreign key'))
    return 'Canal ou referência inválida. Recarregue a página e tente novamente.';
  return 'Erro ao salvar produto. Verifique sua conexão e tente novamente.';
}

export function useProducts() {
  const { tenantId } = useTenant();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!tenantId) { setLoading(false); return; }
    setLoading(true);
    setFetchError(null);
    const { data, error: err } = await supabase
      .from('products')
      .select(`
        id, channel_id, name, sku, description,
        price_cents, stock_qty, min_stock, image_url, status, created_at,
        channels(name)
      `)
      .eq('tenant_id', tenantId)
      .order('created_at', { ascending: false });
    if (err) { setFetchError('Erro ao carregar produtos. Tente recarregar a página.'); setLoading(false); return; }
    const rows = (data ?? []).map((r: any) => ({
      ...r,
      channel_name: r.channels?.name ?? null,
    })) as Product[];
    setProducts(rows);
    setLoading(false);
  }, [tenantId]);

  useEffect(() => { void load(); }, [load]);

  async function addProduct(input: ProductInput): Promise<string> {
    if (!tenantId) throw new Error('Empresa não encontrada. Faça login novamente.');
    const { data, error: err } = await (supabase as any)
      .from('products')
      .insert({ ...input, tenant_id: tenantId })
      .select('id')
      .single();
    if (err) throw new Error(translateProductError(err.message));
    await load();
    return (data as { id: string }).id;
  }

  async function updateProduct(id: string, patch: Partial<ProductInput>): Promise<void> {
    const { error: err } = await (supabase as any)
      .from('products')
      .update({ ...patch, updated_at: new Date().toISOString() })
      .eq('id', id);
    if (err) throw new Error(translateProductError(err.message));
    await load();
  }

  async function deleteProduct(id: string): Promise<void> {
    const { error: err } = await supabase.from('products').delete().eq('id', id);
    if (err) throw new Error(translateProductError(err.message));
    setProducts((prev) => prev.filter((p) => p.id !== id));
  }

  return { products, loading, fetchError, reload: load, addProduct, updateProduct, deleteProduct };
}
